import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApp } from "../appContext.js";
import { registerStoreReset } from "../stores.js";
import { ApiError } from "./apiError.js";
import { API_URL } from "../../config/env.js";

/**
 * THE FOUR THINGS THAT HAVE A PICTURE. One vocabulary, shared with the server's own
 * (<c>AvatarSubjects</c>), because the kind is half of the URL.
 */
export type AvatarKind = "person" | "band" | "org" | "thread";

// A SHARED avatar cache, the `resolveMediaUrl` (useMedia.ts) pattern applied to identity pictures. Before it,
// `useAvatarUrl` was a raw fetch per component instance — so opening the inbox mounted one row per thread,
// each fetching + `createObjectURL`-decoding its avatar afresh, and the same person in two rows fetched
// twice. The inbox open-time hitch was that burst of ~2N authenticated fetches + N main-thread image decodes.
// Now a subject's picture resolves ONCE and every consumer (and every reopen) reads the same object URL.
//  • Keyed by (kind, subjectId, mediaId) — a replace mints a new mediaId, so staleness is structural.
//  • A FAILED resolve is never cached, so a remount retries (a 404 is the designed "no picture" answer).
//  • Object URLs live for the session, bounded by an LRU; an evicted entry is revoked, so blobs cannot
//    accumulate without limit. No revoke on unmount — the cache owns the URL, eviction owns the revoke.
//  • Purged by the switchBand/logout scorch (`registerStoreReset`), so one session's faces can never be
//    served into another's.
const AVATAR_CACHE_MAX = 128;

interface AvatarCacheEntry {
  promise: Promise<string>;
  /** Set once resolved — the object URL to revoke on eviction. */
  url?: string;
}

const avatarCache = new Map<string, AvatarCacheEntry>();

function resolveAvatarUrl(key: string, fetcher: () => Promise<string>): Promise<string> {
  const hit = avatarCache.get(key);
  if (hit) {
    avatarCache.delete(key); // LRU touch: re-insert at the tail
    avatarCache.set(key, hit);
    return hit.promise;
  }
  const entry: AvatarCacheEntry = { promise: fetcher() };
  entry.promise.then(
    (url) => {
      entry.url = url; // remembered so eviction can revoke it
    },
    () => {
      if (avatarCache.get(key) === entry) avatarCache.delete(key); // a failure is never cached
    },
  );
  avatarCache.set(key, entry);
  while (avatarCache.size > AVATAR_CACHE_MAX) {
    const oldest = avatarCache.keys().next().value as string | undefined;
    if (oldest === undefined) break;
    const evicted = avatarCache.get(oldest);
    avatarCache.delete(oldest);
    if (evicted?.url) URL.revokeObjectURL(evicted.url);
  }
  return entry.promise;
}

/** Drop every cached avatar (revoking its object URL). Wired into the switchBand/logout scorch. */
function clearAvatarCache(): void {
  for (const entry of avatarCache.values()) if (entry.url) URL.revokeObjectURL(entry.url);
  avatarCache.clear();
}

registerStoreReset(clearAvatarCache);

/**
 * ANY subject's identity picture as a renderable URL, or undefined.
 *
 * <b>One hook, four subjects — and it replaced three different arrangements.</b> A person's picture came
 * from an authenticated person route; a band's and an org's from `/bands/{bandId}/media/…`, which only
 * somebody who already reached that band could resolve; a chat's could not be fetched at all when the chat
 * had no band to name. They now share one route, because they are one question: what does this thing look
 * like.
 *
 * The serve is AUTHENTICATED and gated per kind server-side, so an `<img src>` cannot hit it directly —
 * fetch with the bearer and hand back an object URL from the shared cache above (the `useMediaObjectUrl`
 * pattern: one fetch per subject, deduped across every consumer and every reopen). A RAW fetch, never the
 * typed client: a 404 here is the designed "no picture" answer and must never trip the global clear-on-401
 * path.
 *
 * `mediaId` is half the cache key. A replace mints a new one, so staleness is structural rather than a rule
 * anybody has to remember — and passing null/undefined resolves nothing, which is exactly what a subject
 * with no picture should render (its initials).
 */
export function useAvatarUrl(
  kind: AvatarKind,
  subjectId: string | null | undefined,
  mediaId: string | null | undefined,
): string | undefined {
  const { tokenProvider } = useApp();
  const [url, setUrl] = useState<string>();

  useEffect(() => {
    if (!subjectId || !mediaId) {
      setUrl(undefined);
      return;
    }
    let live = true;
    resolveAvatarUrl(`${kind}|${subjectId}|${mediaId}`, async () => {
      const bearer = tokenProvider.getBearer();
      const res = await fetch(`${API_URL}/api/v1/avatars/${kind}/${subjectId}?v=${mediaId}`, {
        headers: bearer ? { Authorization: `Bearer ${bearer}` } : undefined,
        credentials: "include",
      });
      if (!res.ok) throw new Error(`avatar ${res.status}`);
      return URL.createObjectURL(await res.blob());
    }).then(
      (resolved) => {
        // No revoke on teardown: the URL belongs to the shared cache now, and a sibling row (or this one on
        // reopen) reads the same one. Eviction owns the revoke.
        if (live) setUrl(resolved);
      },
      () => {
        // No picture is a designed state — the caller falls back to initials.
        if (live) setUrl(undefined);
      },
    );
    return () => {
      live = false;
    };
  }, [kind, subjectId, mediaId, tokenProvider]);

  return url;
}

/**
 * Replace a subject's picture (multipart; jpeg/png/webp ≤ 5 MB). Answers the new media id — the cache-bust
 * key every surface then renders by, which is why the caller reseeds whatever payload carries it.
 *
 * Raw fetch, like every other multipart in this app: the generated client is JSON-bodied.
 *
 * <b>Only `person` and `thread` are uploadable.</b> A band's and an org's picture is DERIVED from an asset
 * the tenant already owns (Brand slot 1, the org logo) by the save that owns that gate — the server answers
 * 404 here for those kinds rather than offering a second door onto the same picture.
 */
export function useSetAvatar(kind: AvatarKind, subjectId: string | null | undefined, invalidate?: unknown[]) {
  const { tokenProvider } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData();
      body.append("file", file);
      const bearer = tokenProvider.getBearer();
      const res = await fetch(`${API_URL}/api/v1/avatars/${kind}/${subjectId}`, {
        method: "PUT",
        headers: bearer ? { Authorization: `Bearer ${bearer}` } : undefined,
        credentials: "include",
        body,
      });
      if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => null));
      return (await res.json()) as { avatarMediaId: string };
    },
    onSuccess: () => {
      if (invalidate) void qc.invalidateQueries({ queryKey: invalidate });
    },
  });
}

/** Remove a subject's picture. Idempotent server-side — clearing one that is not there is a 204. */
export function useClearAvatar(kind: AvatarKind, subjectId: string | null | undefined, invalidate?: unknown[]) {
  const { tokenProvider } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const bearer = tokenProvider.getBearer();
      const res = await fetch(`${API_URL}/api/v1/avatars/${kind}/${subjectId}`, {
        method: "DELETE",
        headers: bearer ? { Authorization: `Bearer ${bearer}` } : undefined,
        credentials: "include",
      });
      if (!res.ok) throw new ApiError(res.status, null);
    },
    onSuccess: () => {
      if (invalidate) void qc.invalidateQueries({ queryKey: invalidate });
    },
  });
}
